import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import PivotTableUIRaw from 'react-pivottable/PivotTableUI'
import TableRenderersRaw from 'react-pivottable/TableRenderers'
import { aggregators as defaultAggregators } from 'react-pivottable/Utilities'
import 'react-pivottable/pivottable.css'
import './TracePivotTable.css'
import { Settings, PanelLeftClose, PanelLeftOpen } from 'lucide-react'

import BaseTile from '@/components/Tile/BaseTile'
import TileSettingsModal from '@/components/common/TileSettingsModal'
import LocalFiltersActiveBadge from '@/components/common/LocalFiltersActiveBadge'
import { useLocalFilters } from '@/hooks/useLocalFilters'
import { useOcelMetadata } from '@/stores/OcelContext'
import TracePivotTableSettings from './TracePivotTableSettings'
import { SortableTableRenderer } from './SortableTableRenderer'
import unitConfigRaw from './unit_config.json'

// Build fast lookups from unit_config.json — one parse at module load, zero runtime cost.
const _unitById = {}
const _unitFamilyByUnitId = {}
for (const family of unitConfigRaw.families) {
  for (const u of family.units) {
    _unitById[u.id] = u
    _unitFamilyByUnitId[u.id] = family
  }
}
function getUnitsForUnit(unitId) {
  const fam = _unitFamilyByUnitId[unitId]
  return fam ? fam.units : []
}
function getFactorToBase(unitId) {
  return _unitById[unitId]?.factor_to_base ?? 1
}

// Unit ids sorted longest-first so "min" matches before "m", "mm" before "m", etc.
const _unitIdsByLength = Object.keys(_unitById).sort((a, b) => b.length - a.length)

// Infer storage unit from attribute name by looking for known unit ids in parentheses.
// Handles naming conventions like "Hauptnutzungszeit (s)" → "s", "Gewicht (kg)" → "kg".
function inferUnitFromName(attrName) {
  if (!attrName) return null
  for (const uid of _unitIdsByLength) {
    const escaped = uid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    if (new RegExp(`\\(${escaped}\\)`).test(attrName)) return uid
  }
  return null
}

// Resolve storage unit: explicit OCEL schema declaration first, name-based inference as fallback.
function lookupStorageUnit(attrName, unitMap) {
  if (!attrName) return null
  return unitMap[attrName] ?? inferUnitFromName(attrName) ?? null
}

// Render unit options for a <select>.
// Family anchor priority: storageUnit (from OCEL schema) → selectedUnit (user's current pick).
// When an anchor is known, only show that family's units.
// When neither is known, show all families so the user can pick any starting point.
// Once the user picks a unit, the family locks until they choose "— no conversion —".
function UnitOptions({ storageUnit, selectedUnit }) {
  const anchor = storageUnit ?? selectedUnit
  if (anchor) {
    return getUnitsForUnit(anchor).map((u) => (
      <option key={u.id} value={u.id}>{u.id}</option>
    ))
  }
  return unitConfigRaw.families.map((fam) => (
    <optgroup key={fam.name} label={fam.name}>
      {fam.units.map((u) => (
        <option key={u.id} value={u.id}>{u.id}</option>
      ))}
    </optgroup>
  ))
}

// CJS-interop: Vite may return { default: Class } instead of Class directly.
const PivotTableUI = PivotTableUIRaw.default ?? PivotTableUIRaw
const TableRenderers = TableRenderersRaw.default ?? TableRenderersRaw

const renderers = {
  'Sortable Table': SortableTableRenderer,
  ...TableRenderers,
}

const TIME_DIMENSION_NAMES = ['Tag', 'Woche', 'Monat', 'Quartal', 'Jahr']

function getISOWeekAndYear(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const day = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - day) // shift to Thursday of the week
  const year = d.getUTCFullYear()
  const jan1 = new Date(Date.UTC(year, 0, 1))
  const week = Math.ceil((((d - jan1) / 86400000) + 1) / 7)
  return { week, year }
}

function deriveTimeDimensions(isoStr) {
  if (!isoStr) return {}
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return {}
  const year  = d.getUTCFullYear()
  const month = d.getUTCMonth() + 1
  const qtr   = Math.ceil(month / 3)
  const { week, year: wYear } = getISOWeekAndYear(d)
  const day = d.getUTCDate()
  return {
    Tag:     `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`,
    Jahr:    String(year),
    Quartal: `Q${qtr}/${year}`,
    Monat:   `${String(month).padStart(2, '0')}/${year}`,
    Woche:   `KW${String(week).padStart(2, '0')}/${wYear}`,
  }
}

const DESIRED_AGGREGATORS = new Set([
  'Sum', 'Count Unique Values', 'Average', 'Median', 'Min', 'Max', 'First', 'Last',
])
const NON_NUMERIC_AGGREGATORS = new Set(['Count Unique Values', 'First', 'Last'])
const AGGREGATOR_NAMES = Object.keys(defaultAggregators).filter((n) => DESIRED_AGGREGATORS.has(n))

const DIVISION_AGGREGATOR = 'Division'
const DIVISION_SIDE_AGGREGATORS = [
  'Sum', 'Average', 'Median', 'Count Unique Values', 'Min', 'Max', 'First', 'Last',
]
const AGGREGATOR_OPTIONS = [...AGGREGATOR_NAMES, DIVISION_AGGREGATOR]

const DEFAULT_DIVISION_CONFIG = {
  numerator: { aggregation: 'Sum', attribute: '', unit: null },
  denominator: { aggregation: 'Sum', attribute: '', unit: null },
  decimals: 2,
  asPercentage: false,
  label: '',
}

const DEFAULT_PIVOT_STATE = {
  rows: [],
  cols: [],
  vals: [],
  aggregatorName: 'Count',
  rendererName: 'Sortable Table',
  valueFilter: {},
  divisionConfig: DEFAULT_DIVISION_CONFIG,
  simpleUnit: null,
}

function clampDecimals(raw, fallback = 2) {
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(0, Math.min(10, parsed))
}

export default function TracePivotTable({ activeFile, onRefresh, onGetHeaderActions, onTitleChange, onRegisterSettingsGetter, initialSettings }) {
  const metadata = useOcelMetadata(activeFile)

  // Build attribute → storage unit map from OCEL schema.
  // Reads both eventTypes and objectTypes so that object attributes (commonly
  // used as numeric measures in division) are also covered.
  const attributeUnitMap = useMemo(() => {
    const map = {}
    for (const et of (metadata?.event_types ?? [])) {
      for (const attr of (et.attributes ?? [])) {
        if (attr.unit) map[attr.name] = attr.unit
      }
    }
    for (const ot of (metadata?.object_types_schema ?? [])) {
      for (const attr of (ot.attributes ?? [])) {
        if (attr.unit) map[attr.name] = attr.unit
      }
    }
    return map
  }, [metadata])

  const [objectTypeFilter, setObjectTypeFilter] = useState(null)
  const [activityFilter, setActivityFilter] = useState(null)
  const [componentTitle, setComponentTitle] = useState('Pivot Table')

  const [starSchema, setStarSchema] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [pivotState, setPivotState] = useState(DEFAULT_PIVOT_STATE)
  const [decimalsInput, setDecimalsInput] = useState(String(DEFAULT_DIVISION_CONFIG.decimals))
  const [labelInput, setLabelInput] = useState(DEFAULT_DIVISION_CONFIG.label)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [dimUnitConfig, setDimUnitConfig] = useState({})

  const [draftObjectType, setDraftObjectType] = useState(null)
  const [draftActivity, setDraftActivity] = useState(null)
  const [draftTitle, setDraftTitle] = useState('Pivot Table')

  const {
    api,
    currentFilters,
    localFilters,
    updateLocalFilter,
    hasLocalFilters,
    localFilterCount,
    clearLocalFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null)

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.objectTypeFilter !== undefined) setObjectTypeFilter(initialSettings.objectTypeFilter);
    if (initialSettings.activityFilter !== undefined) setActivityFilter(initialSettings.activityFilter);
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.pivotState !== undefined) setPivotState(initialSettings.pivotState);
    if (initialSettings.dimUnitConfig !== undefined) setDimUnitConfig(initialSettings.dimUnitConfig);
    const ps = initialSettings.pivotState;
    if (ps && (ps.rows?.length > 0 || ps.cols?.length > 0)) setShowControls(false);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({ objectTypeFilter, activityFilter, componentTitle, pivotState, dimUnitConfig, localFilters }));
  }, [objectTypeFilter, activityFilter, componentTitle, pivotState, dimUnitConfig, localFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTitleChangeRef = useRef(onTitleChange)
  useEffect(() => { onTitleChangeRef.current = onTitleChange })
  const onRefreshRef = useRef(onRefresh)
  useEffect(() => { onRefreshRef.current = onRefresh })
  const onGetHeaderActionsRef = useRef(onGetHeaderActions)
  useEffect(() => { onGetHeaderActionsRef.current = onGetHeaderActions })
  const requestSeqRef = useRef(0)
  const abortRef = useRef(null)
  const hasDataRef = useRef(false)
  const loadingTimerRef = useRef(null)

  useEffect(() => {
    hasDataRef.current = Boolean(starSchema)
  }, [starSchema])

  useEffect(() => {
    setLabelInput(pivotState.divisionConfig.label)
  }, [pivotState.divisionConfig.label])

  // Sync dimUnitConfig: add storage unit for newly active dims, prune removed dims.
  useEffect(() => {
    const activeDims = [...pivotState.rows, ...pivotState.cols]
    setDimUnitConfig(prev => {
      const next = {}
      for (const attr of activeDims) {
        const storageUnit = lookupStorageUnit(attr, attributeUnitMap)
        if (storageUnit) next[attr] = prev[attr] ?? storageUnit
      }
      if (JSON.stringify(next) === JSON.stringify(prev)) return prev
      return next
    })
  }, [pivotState.rows, pivotState.cols, attributeUnitMap])

  const isDivision = pivotState.aggregatorName === DIVISION_AGGREGATOR
  const hasCompleteDivisionConfig =
    isDivision &&
    !!pivotState.divisionConfig.numerator.attribute &&
    !!pivotState.divisionConfig.denominator.attribute

  const divisionComputeSignature = useMemo(() => {
    if (!hasCompleteDivisionConfig) return null
    // unit is display-only — excluded so unit changes don't trigger a backend re-fetch.
    return JSON.stringify({
      rows: pivotState.rows,
      cols: pivotState.cols,
      numerator: {
        aggregation: pivotState.divisionConfig.numerator.aggregation,
        attribute: pivotState.divisionConfig.numerator.attribute,
      },
      denominator: {
        aggregation: pivotState.divisionConfig.denominator.aggregation,
        attribute: pivotState.divisionConfig.denominator.attribute,
      },
    })
  }, [
    hasCompleteDivisionConfig,
    pivotState.rows,
    pivotState.cols,
    pivotState.divisionConfig.numerator.aggregation,
    pivotState.divisionConfig.numerator.attribute,
    pivotState.divisionConfig.denominator.aggregation,
    pivotState.divisionConfig.denominator.attribute,
  ])

  const fetchData = useCallback(async () => {
    if (!activeFile) return

    abortRef.current?.abort()
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current)
      loadingTimerRef.current = null
    }
    const controller = new AbortController()
    abortRef.current = controller
    const requestSeq = ++requestSeqRef.current

    if (hasDataRef.current) {
      setLoading(false)
      loadingTimerRef.current = setTimeout(() => {
        if (requestSeq === requestSeqRef.current) {
          setLoading(true)
        }
      }, 200)
    } else {
      setLoading(true)
    }
    setError(null)
    try {
      const body = {
        object_type: objectTypeFilter || undefined,
        activity: activityFilter || undefined,
      }
      if (hasCompleteDivisionConfig) {
        // Strip display-only `unit` field — backend schema only accepts aggregation + attribute.
        const { unit: _nu, ...numeratorPayload } = pivotState.divisionConfig.numerator
        const { unit: _du, ...denominatorPayload } = pivotState.divisionConfig.denominator
        body.division_measure = {
          numerator: numeratorPayload,
          denominator: denominatorPayload,
          rows: pivotState.rows,
          cols: pivotState.cols,
        }
      }

      const starResult = await api.post('/traces/event-object-star', body, { signal: controller.signal })

      if (requestSeq !== requestSeqRef.current) return

      setStarSchema(starResult)
    } catch (err) {
      if (controller.signal.aborted || requestSeq !== requestSeqRef.current) return
      setError(err?.message || 'Failed to load traces data')
    } finally {
      if (requestSeq === requestSeqRef.current) {
        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current)
          loadingTimerRef.current = null
        }
        if (abortRef.current === controller) abortRef.current = null
        setLoading(false)
      }
    }
    // pivotState.{rows,cols,numerator,denominator} are captured via divisionComputeSignature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, activeFile, objectTypeFilter, activityFilter, divisionComputeSignature])

  useEffect(() => {
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current)
        loadingTimerRef.current = null
      }
      abortRef.current?.abort()
      requestSeqRef.current += 1
    }
  }, [])

  const allFlatRows = useMemo(() => {
    if (!starSchema?.bridge) return []
    const eventMap = new Map((starSchema.events ?? []).map((e) => [e.event_id, e]))
    const dimMaps = {}
    for (const [otype, dim] of Object.entries(starSchema.dimensions ?? {})) {
      dimMaps[otype] = new Map((dim.rows ?? []).map((r) => [r.object_id, r]))
    }

    const o2oRelated = new Map()
    for (const rel of (starSchema.o2o_bridge ?? [])) {
      if (!o2oRelated.has(rel.source_oid)) o2oRelated.set(rel.source_oid, new Set())
      o2oRelated.get(rel.source_oid).add(rel.target_oid)
      if (!o2oRelated.has(rel.target_oid)) o2oRelated.set(rel.target_oid, new Set())
      o2oRelated.get(rel.target_oid).add(rel.source_oid)
    }

    const rows = []
    for (const br of starSchema.bridge) {
      const ev = eventMap.get(br.event_id) ?? {}
      const flat = { ...br, ...ev }
      for (const [, dm] of Object.entries(dimMaps)) {
        const dimRow = dm.get(br.object_id)
        if (dimRow) Object.assign(flat, dimRow)
      }

      const timeDims = deriveTimeDimensions(flat.timestamp ?? null)
      Object.assign(flat, timeDims)

      const related = o2oRelated.get(br.object_id)
      if (!related || related.size === 0) {
        rows.push(flat)
        continue
      }

      const combined = { ...flat }
      for (const relId of related) {
        for (const [, dm] of Object.entries(dimMaps)) {
          const relDimRow = dm.get(relId)
          if (relDimRow) {
            for (const [key, val] of Object.entries(relDimRow)) {
              if (
                key !== 'object_id' &&
                key !== 'object_type' &&
                (combined[key] == null || combined[key] === undefined)
              ) {
                combined[key] = val
              }
            }
            break
          }
        }
      }
      rows.push(combined)
    }
    return rows
  }, [starSchema])

  // Hide rows where the first row dimension is null — objects with no relationship to
  // the leading dimension don't belong in a pivot organised by that dimension.
  const flatRows = useMemo(() => {
    const leadingDim = pivotState.rows[0]
    if (!leadingDim || !allFlatRows.length) return allFlatRows
    return allFlatRows.filter((r) => r[leadingDim] != null && r[leadingDim] !== '')
  }, [allFlatRows, pivotState.rows])

  // Map original attr name → display name whenever a storage unit is known.
  // Always shows the unit in the header, even at the default (storage) unit.
  // e.g. "Hauptnutzungszeit (s)" at storage → "Hauptnutzungszeit (s) [s]"
  //      "Hauptnutzungszeit (s)" converted  → "Hauptnutzungszeit (s) [h]"
  const dimDisplayName = useMemo(() => {
    const map = {}
    for (const [attr, displayUnit] of Object.entries(dimUnitConfig)) {
      const storageUnit = lookupStorageUnit(attr, attributeUnitMap)
      if (storageUnit) {
        map[attr] = `${attr} [${displayUnit ?? storageUnit}]`
      }
    }
    return map
  }, [dimUnitConfig, attributeUnitMap])

  // Reverse map for onPivotChange: display name → original name.
  const dimOriginalNameRef = useRef({})
  useEffect(() => {
    const rev = {}
    for (const [orig, display] of Object.entries(dimDisplayName)) rev[display] = orig
    dimOriginalNameRef.current = rev
  }, [dimDisplayName])

  // Rename and convert dimension columns in flat rows before passing to PivotTableUI.
  // Every dim with a known unit gets its key renamed to the display name (which
  // includes the unit suffix). Value conversion is applied only when the selected
  // unit differs from the storage unit.
  const convertedFlatRows = useMemo(() => {
    if (!flatRows.length) return flatRows
    const renames = Object.entries(dimDisplayName).map(([attr, displayName]) => {
      const storageUnit = lookupStorageUnit(attr, attributeUnitMap)
      const displayUnit = dimUnitConfig[attr]
      const factor =
        storageUnit && displayUnit && storageUnit !== displayUnit
          ? getFactorToBase(storageUnit) / getFactorToBase(displayUnit)
          : 1
      return { attr, displayName, factor }
    })
    if (renames.length === 0) return flatRows
    return flatRows.map(row => {
      const newRow = { ...row }
      for (const { attr, displayName, factor } of renames) {
        const val = row[attr]
        delete newRow[attr]
        if (val !== null && val !== undefined) {
          const num = Number(val)
          newRow[displayName] = !isNaN(num) && factor !== 1 ? parseFloat((num * factor).toFixed(2)) : val
        } else {
          newRow[displayName] = null
        }
      }
      return newRow
    })
  }, [flatRows, dimUnitConfig, dimDisplayName, attributeUnitMap])

  const allColumns = useMemo(() => {
    if (!starSchema) return []
    const cols = new Set()
    for (const c of (starSchema.event_columns ?? [])) cols.add(c)
    cols.add('activity')
    cols.add('timestamp')
    for (const c of (starSchema.bridge_columns ?? [])) cols.add(c)
    for (const dim of Object.values(starSchema.dimensions ?? {})) {
      for (const c of (dim.columns ?? [])) {
        if (c !== 'object_id') cols.add(c)
      }
    }
    return Array.from(cols)
  }, [starSchema])

  useEffect(() => {
    onTitleChangeRef.current?.(componentTitle)
  }, [componentTitle])

  useEffect(() => {
    onRefreshRef.current?.(fetchData)
  }, [fetchData])

  const onPivotChange = useCallback((s) => {
    const mapBack = (names) => (names ?? []).map(n => dimOriginalNameRef.current[n] ?? n)
    setPivotState((prev) => {
      const inDivision = prev.aggregatorName === DIVISION_AGGREGATOR
      return {
        ...prev,
        rows: mapBack(s.rows),
        cols: mapBack(s.cols),
        vals: inDivision ? prev.vals : (s.vals ?? []),
        aggregatorName: inDivision ? DIVISION_AGGREGATOR : (s.aggregatorName ?? 'Count'),
        rendererName: s.rendererName ?? 'Sortable Table',
        valueFilter: s.valueFilter ?? {},
      }
    })
  }, [])

  const availableColumns = allColumns
  const numericColumns = starSchema?.numeric_columns || []
  const hasNoDimensions = pivotState.rows.length === 0 && pivotState.cols.length === 0

  const handleRendererChange = (e) => setPivotState((p) => ({ ...p, rendererName: e.target.value }))
  const handleAggregatorChange = (e) => {
    const newAgg = e.target.value
    setPivotState((p) => {
      const valid = NON_NUMERIC_AGGREGATORS.has(newAgg) ? availableColumns : numericColumns
      const vals = p.vals[0] && !valid.includes(p.vals[0]) ? [] : p.vals
      return { ...p, aggregatorName: newAgg, vals }
    })
  }
  const handleValsChange = (vals) => {
    const attrName = vals[0] ?? null
    const storageUnit = attrName ? lookupStorageUnit(attrName, attributeUnitMap) : null
    setPivotState((p) => ({ ...p, vals, simpleUnit: storageUnit }))
  }

  const validAttributesForAggregation = useCallback(
    (aggregation) => {
      if (NON_NUMERIC_AGGREGATORS.has(aggregation)) return availableColumns
      return numericColumns
    },
    [availableColumns, numericColumns],
  )

  const updateDivisionConfig = (patch) => {
    setPivotState((p) => ({
      ...p,
      divisionConfig: { ...p.divisionConfig, ...patch },
    }))
  }

  const commitDecimalsInput = () => {
    const decimals = clampDecimals(decimalsInput, DEFAULT_DIVISION_CONFIG.decimals)
    setDecimalsInput(String(decimals))
    updateDivisionConfig({ decimals })
  }

  const commitLabelInput = () => {
    if (labelInput !== pivotState.divisionConfig.label) {
      updateDivisionConfig({ label: labelInput })
    }
  }

  const updateDivisionSide = (side, patch) => {
    setPivotState((p) => {
      const next = { ...p.divisionConfig[side], ...patch }
      if ('aggregation' in patch) {
        const valid = NON_NUMERIC_AGGREGATORS.has(patch.aggregation) ? availableColumns : numericColumns
        if (next.attribute && !valid.includes(next.attribute)) {
          next.attribute = ''
          next.unit = null
        }
      }
      if ('attribute' in patch) {
        // Pre-select storage unit from OCEL schema (or inferred from name) when attribute changes.
        next.unit = lookupStorageUnit(patch.attribute, attributeUnitMap)
      }
      const newConfig = { ...p.divisionConfig, [side]: next }
      const numEff = newConfig.numerator.unit ?? lookupStorageUnit(newConfig.numerator.attribute, attributeUnitMap)
      const denEff = newConfig.denominator.unit ?? lookupStorageUnit(newConfig.denominator.attribute, attributeUnitMap)
      const compatible = !!(numEff && denEff && numEff === denEff)
      return {
        ...p,
        divisionConfig: {
          ...newConfig,
          asPercentage: compatible ? p.divisionConfig.asPercentage : false,
        },
      }
    })
  }

  // Scalar multiplier applied on the frontend to convert from storage unit to display unit.
  // For division: multiply = (numStorageFactor * denTargetFactor) / (numTargetFactor * denStorageFactor)
  // For simple:   multiply = storageFactor / targetFactor
  const unitMultiplier = useMemo(() => {
    if (isDivision) {
      const { numerator: num, denominator: den } = pivotState.divisionConfig
      if (!num.attribute || !den.attribute) return 1
      const numStorage = lookupStorageUnit(num.attribute, attributeUnitMap)
      const denStorage = lookupStorageUnit(den.attribute, attributeUnitMap)
      if (!numStorage && !denStorage) return 1
      const numTarget = num.unit ?? numStorage
      const denTarget = den.unit ?? denStorage
      const numSF = getFactorToBase(numStorage)
      const numTF = getFactorToBase(numTarget)
      const denSF = getFactorToBase(denStorage)
      const denTF = getFactorToBase(denTarget)
      const m = (numSF * denTF) / (numTF * denSF)
      return Number.isFinite(m) && m !== 0 ? m : 1
    } else {
      const attrName = pivotState.vals[0]
      if (!attrName) return 1
      const storage = lookupStorageUnit(attrName, attributeUnitMap)
      if (!storage) return 1
      // Count Unique Values / Count results are dimensionless — don't convert.
      if (pivotState.aggregatorName === 'Count Unique Values' || pivotState.aggregatorName === 'Count') return 1
      const target = pivotState.simpleUnit ?? storage
      const m = getFactorToBase(storage) / getFactorToBase(target)
      return Number.isFinite(m) && m !== 0 ? m : 1
    }
  }, [isDivision, pivotState, attributeUnitMap])

  const isPercentageCompatible = useMemo(() => {
    const { numerator: num, denominator: den } = pivotState.divisionConfig
    if (!num.attribute || !den.attribute) return false
    const numEff = num.unit ?? lookupStorageUnit(num.attribute, attributeUnitMap)
    const denEff = den.unit ?? lookupStorageUnit(den.attribute, attributeUnitMap)
    if (!numEff || !denEff) return false
    return numEff === denEff
  }, [pivotState.divisionConfig, attributeUnitMap])

  const displayUnitLabel = useMemo(() => {
    if (isDivision) {
      const { numerator: num, denominator: den } = pivotState.divisionConfig
      const numStorage = lookupStorageUnit(num.attribute, attributeUnitMap)
      const denStorage = lookupStorageUnit(den.attribute, attributeUnitMap)
      const numUnit = num.unit ?? numStorage
      const denUnit = den.unit ?? denStorage
      if (pivotState.divisionConfig.asPercentage && isPercentageCompatible) return '%'
      if (!numUnit && !denUnit) return null
      if (numUnit && denUnit) return `${numUnit}/${denUnit}`
      return numUnit ?? denUnit
    } else {
      const attrName = pivotState.vals[0]
      if (!attrName) return null
      const storage = lookupStorageUnit(attrName, attributeUnitMap)
      if (!storage) return null
      if (pivotState.aggregatorName === 'Count Unique Values' || pivotState.aggregatorName === 'Count') return null
      return pivotState.simpleUnit ?? storage
    }
  }, [isDivision, pivotState, attributeUnitMap, isPercentageCompatible])

  const handleOpenSettings = useCallback(() => {
    setDraftObjectType(objectTypeFilter)
    setDraftActivity(activityFilter)
    setDraftTitle(componentTitle)
    startDraftMode()
    setIsSettingsOpen(true)
  }, [objectTypeFilter, activityFilter, componentTitle, startDraftMode])

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {hasLocalFilters && (
          <LocalFiltersActiveBadge
            hasLocalFilters={hasLocalFilters}
            localFilterCount={localFilterCount}
            onClearLocalFilters={clearLocalFilters}
          />
        )}
        <button
          onClick={handleOpenSettings}
          title="Settings"
          className="rounded p-1 hover:bg-muted transition-colors"
        >
          <Settings className="h-4 w-4" />
        </button>
      </div>
    ),
    [handleOpenSettings, hasLocalFilters, localFilterCount, clearLocalFilters],
  )

  useEffect(() => {
    onGetHeaderActionsRef.current?.(headerActions)
  }, [headerActions])

  // Force header re-render when the local-filter badge appears or disappears
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onGetHeaderActionsRef.current?.(headerActions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLocalFilters])

  const handleSettingsOpenChange = (val) => {
    setIsSettingsOpen(val)
    if (!val) cancelDraftFilters()
  }

  const handleApplySettings = () => {
    setObjectTypeFilter(draftObjectType)
    setActivityFilter(draftActivity)
    setComponentTitle(draftTitle)
    applyDraftFilters()
  }

  useEffect(() => {
    if (activeFile) fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFile, objectTypeFilter, activityFilter, currentFilters, divisionComputeSignature])

  const availableObjectTypes = metadata?.object_types
    ? Object.keys(metadata.object_types)
    : []
  const availableActivities = metadata?.activity_types
    ? Object.keys(metadata.activity_types)
    : []

  const pivotKey = useMemo(() => {
    const dimSig = Object.values(dimDisplayName).sort().join(',')
    return `${isDivision ? 'division' : 'standard'}|${dimSig}`
  }, [isDivision, dimDisplayName])

  const pivotRef = useRef(null)

  // Callback ref: fires immediately when PivotTableUI mounts (not deferred like useEffect).
  // Intercepts setState so any unusedOrder update is sorted before being applied —
  // covering both pool-internal drags and drag-back-from-rows/cols.
  const pivotCallbackRef = useCallback((instance) => {
    pivotRef.current = instance
    if (!instance) return
    const origSetState = instance.setState.bind(instance)
    instance.setState = (updater, callback) => {
      if (
        updater !== null &&
        typeof updater === 'object' &&
        !Array.isArray(updater) &&
        Array.isArray(updater.unusedOrder)
      ) {
        const sorted = [...updater.unusedOrder].sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        )
        origSetState({ ...updater, unusedOrder: sorted }, callback)
      } else {
        origSetState(updater, callback)
      }
    }
  }, [])

  useEffect(() => {
    if (!pivotRef.current || !convertedFlatRows.length) return
    if (pivotRef.current.state.unusedOrder.length > 0) return
    const sorted = Object.keys(convertedFlatRows[0]).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    )
    pivotRef.current.setState({ unusedOrder: sorted })
  }, [pivotKey, convertedFlatRows])

  const divisionFormat = useMemo(
    () => ({
      decimals: pivotState.divisionConfig.decimals,
      asPercentage: pivotState.divisionConfig.asPercentage,
      label: pivotState.divisionConfig.label,
    }),
    [pivotState.divisionConfig],
  )

  return (
    <>
      <BaseTile
        loading={loading}
        error={error}
        placeholder={!starSchema ? 'Configure and load traces data' : undefined}
        padding="none"
      >
        {starSchema && flatRows.length > 0 && (
          <div className={`trace-pivot-wrapper flex flex-row h-full min-w-0 overflow-hidden${showControls ? '' : ' controls-hidden'}${hasNoDimensions ? ' no-dimensions' : ''}`}>
            <div className="flex-shrink-0 flex items-start pt-2 pl-1">
              <button
                onClick={() => setShowControls((v) => !v)}
                title={showControls ? 'Hide options' : 'Show options'}
                className="rounded p-1 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                {showControls ? (
                  <PanelLeftClose className="h-4 w-4" />
                ) : (
                  <PanelLeftOpen className="h-4 w-4" />
                )}
              </button>
            </div>
            <div className="overflow-auto flex-1 min-w-0 p-2 flex gap-4 items-start">
              {showControls && (
                <div className="pivot-custom-controls flex-shrink-0 flex flex-col gap-3">
                  <select value={pivotState.rendererName} onChange={handleRendererChange}>
                    {Object.keys(renderers).map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <div className="flex flex-col gap-1">
                    <span className="pivot-control-label">Aggregation</span>
                    <select value={pivotState.aggregatorName} onChange={handleAggregatorChange}>
                      {AGGREGATOR_OPTIONS.map((a) => <option key={a}>{a}</option>)}
                    </select>
                  </div>

                  {!isDivision && (
                    <div className="flex flex-col gap-1">
                      <span className="pivot-control-label">Attribute</span>
                      <select
                        value={pivotState.vals[0] ?? ''}
                        onChange={(e) => handleValsChange(e.target.value ? [e.target.value] : [])}
                      >
                        <option value="">None</option>
                        {validAttributesForAggregation(pivotState.aggregatorName).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {!isDivision &&
                    pivotState.vals[0] &&
                    lookupStorageUnit(pivotState.vals[0], attributeUnitMap) &&
                    pivotState.aggregatorName !== 'Count' &&
                    pivotState.aggregatorName !== 'Count Unique Values' && (
                    <div className="flex flex-col gap-1">
                      <span className="pivot-control-label">Unit</span>
                      <select
                        value={pivotState.simpleUnit ?? lookupStorageUnit(pivotState.vals[0], attributeUnitMap)}
                        onChange={(e) => setPivotState((p) => ({ ...p, simpleUnit: e.target.value }))}
                      >
                        {getUnitsForUnit(lookupStorageUnit(pivotState.vals[0], attributeUnitMap)).map((u) => (
                          <option key={u.id} value={u.id}>{u.id}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {isDivision && (
                    <div className="pivot-division-panel flex flex-col gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="pivot-control-label">Numerator agg</span>
                        <select
                          value={pivotState.divisionConfig.numerator.aggregation}
                          onChange={(e) =>
                            updateDivisionSide('numerator', { aggregation: e.target.value })
                          }
                        >
                          {DIVISION_SIDE_AGGREGATORS.map((a) => <option key={a}>{a}</option>)}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="pivot-control-label">Numerator attribute</span>
                        <select
                          value={pivotState.divisionConfig.numerator.attribute}
                          onChange={(e) =>
                            updateDivisionSide('numerator', { attribute: e.target.value })
                          }
                        >
                          <option value="">-- select --</option>
                          {validAttributesForAggregation(
                            pivotState.divisionConfig.numerator.aggregation,
                          ).map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>

                      {pivotState.divisionConfig.numerator.attribute &&
                        pivotState.divisionConfig.numerator.aggregation !== 'Count Unique Values' && (
                        <div className="flex flex-col gap-1">
                          <span className="pivot-control-label">Numerator unit</span>
                          <select
                            value={pivotState.divisionConfig.numerator.unit ?? ''}
                            onChange={(e) =>
                              updateDivisionSide('numerator', { unit: e.target.value || null })
                            }
                          >
                            <option value="">— no conversion —</option>
                            <UnitOptions storageUnit={lookupStorageUnit(pivotState.divisionConfig.numerator.attribute, attributeUnitMap)} selectedUnit={pivotState.divisionConfig.numerator.unit} />
                          </select>
                        </div>
                      )}

                      <div className="flex flex-col gap-1">
                        <span className="pivot-control-label">Denominator agg</span>
                        <select
                          value={pivotState.divisionConfig.denominator.aggregation}
                          onChange={(e) =>
                            updateDivisionSide('denominator', { aggregation: e.target.value })
                          }
                        >
                          {DIVISION_SIDE_AGGREGATORS.map((a) => <option key={a}>{a}</option>)}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="pivot-control-label">Denominator attribute</span>
                        <select
                          value={pivotState.divisionConfig.denominator.attribute}
                          onChange={(e) =>
                            updateDivisionSide('denominator', { attribute: e.target.value })
                          }
                        >
                          <option value="">-- select --</option>
                          {validAttributesForAggregation(
                            pivotState.divisionConfig.denominator.aggregation,
                          ).map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>

                      {pivotState.divisionConfig.denominator.attribute &&
                        pivotState.divisionConfig.denominator.aggregation !== 'Count Unique Values' && (
                        <div className="flex flex-col gap-1">
                          <span className="pivot-control-label">Denominator unit</span>
                          <select
                            value={pivotState.divisionConfig.denominator.unit ?? ''}
                            onChange={(e) => updateDivisionSide('denominator', { unit: e.target.value || null })}
                          >
                            <option value="">— no conversion —</option>
                            <UnitOptions storageUnit={lookupStorageUnit(pivotState.divisionConfig.denominator.attribute, attributeUnitMap)} selectedUnit={pivotState.divisionConfig.denominator.unit} />
                          </select>
                        </div>
                      )}

                      <div className="flex flex-col gap-1">
                        <span className="pivot-control-label">Label</span>
                        <input
                          type="text"
                          value={labelInput}
                          onChange={(e) => setLabelInput(e.target.value)}
                          onBlur={commitLabelInput}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur()
                            }
                          }}
                          placeholder="Division"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="pivot-control-label">Decimals</span>
                        <input
                          type="number"
                          min={0}
                          max={10}
                          value={decimalsInput}
                          onChange={(e) => setDecimalsInput(e.target.value)}
                          onBlur={commitDecimalsInput}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur()
                            }
                          }}
                        />
                      </div>
                      <label className={`flex items-center gap-2 text-xs ${isPercentageCompatible ? 'text-foreground' : 'text-muted-foreground'}`}>
                        <input
                          type="checkbox"
                          checked={pivotState.divisionConfig.asPercentage}
                          onChange={(e) => updateDivisionConfig({ asPercentage: e.target.checked })}
                          disabled={!isPercentageCompatible}
                        />
                        Display as percentage
                        {!isPercentageCompatible &&
                          pivotState.divisionConfig.numerator.attribute &&
                          pivotState.divisionConfig.denominator.attribute && (
                          <span className="text-muted-foreground/70 text-[10px]">(incompatible units)</span>
                        )}
                      </label>
                    </div>
                  )}

                  {Object.keys(dimUnitConfig).length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="pivot-control-label">Dimension units</span>
                      {Object.entries(dimUnitConfig).map(([attr, displayUnit]) => {
                        const storageUnit = lookupStorageUnit(attr, attributeUnitMap)
                        return (
                          <div key={attr} className="flex flex-col gap-1">
                            <span className="pivot-control-label" style={{ fontWeight: 'normal', fontSize: '0.7rem' }}>{attr}</span>
                            <select
                              value={displayUnit ?? ''}
                              onChange={e => {
                                const newUnit = e.target.value
                                setDimUnitConfig(prev => ({ ...prev, [attr]: newUnit }))
                                setPivotState(p => ({ ...p, valueFilter: {} }))
                              }}
                            >
                              <UnitOptions storageUnit={storageUnit} selectedUnit={displayUnit} />
                            </select>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
              <PivotTableUI
                key={pivotKey}
                ref={pivotCallbackRef}
                data={convertedFlatRows}
                starSchema={starSchema}
                rows={pivotState.rows.map(a => dimDisplayName[a] ?? a)}
                cols={pivotState.cols.map(a => dimDisplayName[a] ?? a)}
                vals={isDivision ? [] : pivotState.vals}
                aggregatorName={isDivision ? 'Count' : pivotState.aggregatorName}
                rendererName={pivotState.rendererName}
                valueFilter={pivotState.valueFilter}
                renderers={renderers}
                onChange={onPivotChange}
                unusedOrientationCutoff={0}
                divisionMode={isDivision}
                divisionCells={
                  isDivision && hasCompleteDivisionConfig
                    ? starSchema?.division_cells ?? null
                    : null
                }
                divisionFormat={isDivision ? divisionFormat : null}
                unitMultiplier={unitMultiplier}
                unitLabel={displayUnitLabel}
              />
            </div>
          </div>
        )}

        {starSchema && flatRows.length === 0 && !loading && (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground p-6">
            No data matches the current filters.
          </div>
        )}
      </BaseTile>

      <TileSettingsModal
        open={isSettingsOpen}
        onOpenChange={handleSettingsOpenChange}
        title="Configure Pivot Table"
        getInitialValues={() => ({
          objectTypeFilter,
          activityFilter,
          componentTitle,
        })}
        onApply={handleApplySettings}
      >
        <TracePivotTableSettings
          objectTypeFilter={draftObjectType}
          onObjectTypeChange={setDraftObjectType}
          activityFilter={draftActivity}
          onActivityFilterChange={setDraftActivity}
          componentTitle={draftTitle}
          onTitleChange={setDraftTitle}
          availableObjectTypes={availableObjectTypes}
          availableActivities={availableActivities}
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          activeFile={activeFile}
        />
      </TileSettingsModal>
    </>
  )
}
